/**
 * TailwindClassInput - Autocomplete input for Tailwind CSS class names
 *
 * Features:
 * - Token-level autocomplete (detects word at cursor)
 * - Prefix + contains search with sorted results
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Supports responsive prefixes (sm:, md:, lg:, etc.)
 */

import { useState, useRef, useCallback, useEffect } from "react";

import { cn } from "@/lib/utils";
import {
  searchTailwindClasses,
  getCurrentToken,
  replaceToken,
} from "@/lib/visual-editor/tailwind-classes";

interface TailwindClassInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function TailwindClassInput({
  value,
  onChange,
  disabled = false,
  placeholder = "e.g. text-lg font-bold text-red-500",
  className,
}: TailwindClassInputProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<{ token: string; start: number; end: number }>({
    token: "",
    start: 0,
    end: 0,
  });

  const updateSuggestions = useCallback((inputValue: string) => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart ?? inputValue.length;
    const tokenInfo = getCurrentToken(inputValue, cursorPos);
    tokenRef.current = tokenInfo;

    if (tokenInfo.token.length >= 2) {
      const results = searchTailwindClasses(tokenInfo.token, 50);
      setSuggestions(results);
      setSelectedIndex(0);
      setShowSuggestions(results.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      updateSuggestions(newValue);
    },
    [onChange, updateSuggestions],
  );

  const selectSuggestion = useCallback(
    (suggestion: string) => {
      const { start, end } = tokenRef.current;
      const newValue = replaceToken(value, start, end, suggestion);
      onChange(newValue);
      setSuggestions([]);
      setShowSuggestions(false);

      // Restore focus and set cursor after replacement
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const newCursorPos = start + suggestion.length;
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!showSuggestions || suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1,
          );
          break;
        case "Enter":
          e.preventDefault();
          selectSuggestion(suggestions[selectedIndex]);
          break;
        case "Escape":
          e.preventDefault();
          setShowSuggestions(false);
          break;
        case "Tab":
          if (suggestions.length > 0) {
            e.preventDefault();
            selectSuggestion(suggestions[selectedIndex]);
          }
          break;
      }
    },
    [showSuggestions, suggestions, selectedIndex, selectSuggestion],
  );

  // Scroll active suggestion into view
  useEffect(() => {
    if (!listRef.current || !showSuggestions) return;
    const activeItem = listRef.current.children[selectedIndex] as HTMLElement;
    if (activeItem) {
      activeItem.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, showSuggestions]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        inputRef.current &&
        !inputRef.current.contains(target) &&
        listRef.current &&
        !listRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => updateSuggestions(value)}
        onBlur={() => {
          // Delay to allow click on suggestion
          setTimeout(() => setShowSuggestions(false), 200);
        }}
        disabled={disabled}
        placeholder={placeholder}
        rows={2}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2",
          "text-xs ring-offset-background",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "resize-none",
          className,
        )}
      />

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={listRef}
          className={cn(
            "absolute left-0 right-0 z-50 mt-1",
            "max-h-48 overflow-y-auto",
            "rounded-md border border-border bg-popover shadow-md",
          )}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              className={cn(
                "flex w-full items-center px-3 py-1.5 text-left text-xs",
                "hover:bg-accent hover:text-accent-foreground",
                "transition-colors cursor-pointer",
                index === selectedIndex && "bg-accent text-accent-foreground",
              )}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur before click registers
                selectSuggestion(suggestion);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="font-mono">{suggestion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
